import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Wrench, Gauge, Lock, Paperclip } from "lucide-react";
import axios from "axios";
import { API, eur, km, dateFr } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";

const LABELS = { neutre: "À renseigner", vert: "À jour", orange: "Bientôt", rouge: "Dépassé" };

export default function SharedLogbook() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/share/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setError("Ce lien de partage est invalide ou a été désactivé par son propriétaire."));
  }, [token]);

  if (error)
    return (
      <div className="min-h-screen grid place-items-center px-4 bg-background">
        <div data-testid="share-error" className="max-w-md text-center bg-white border border-slate-200 rounded-xl p-8 shadow-sm">
          <Lock className="h-8 w-8 text-slate-400 mx-auto" />
          <p className="mt-4 font-display text-lg font-semibold text-slate-800">Lien indisponible</p>
          <p className="mt-2 text-sm text-slate-500">{error}</p>
        </div>
      </div>
    );

  if (!data) return <div className="min-h-screen grid place-items-center text-slate-500">Chargement…</div>;

  const v = data.vehicle;
  const ft = v.fiche_technique || {};

  return (
    <div data-testid="shared-logbook-page" className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="h-9 w-9 rounded-xl bg-blue-600 text-white grid place-items-center">
              <Wrench className="h-5 w-5" />
            </span>
            <span className="font-display font-bold text-lg text-slate-900">Mon Entretien Auto</span>
          </div>
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500">
            <Lock className="h-3.5 w-3.5" /> Consultation en lecture seule
          </span>
        </div>
      </header>

      <main className="w-full max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-10">
        <section className="animate-fade-up">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Carnet d'entretien</p>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            {v.marque} {v.modele}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            {v.immatriculation && (
              <span className="px-2.5 py-1 rounded-md border border-slate-300 bg-slate-50 font-display text-sm font-bold tracking-widest text-slate-700">
                {v.immatriculation}
              </span>
            )}
            <span className="inline-flex items-center gap-2 text-slate-600 text-sm">
              <Gauge className="h-4 w-4 text-slate-400" /> {km(v.kilometrage)} · {v.annee}
              {v.carburant ? ` · ${v.carburant}` : ""}
            </span>
            <StatusBadge status={data.statut_global} label={LABELS[data.statut_global]}
              testId="shared-global-status" />
          </div>
        </section>

        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            ["Opérations", data.nb_operations],
            ["Dépenses cumulées", eur(data.total_depenses)],
            ["Motorisation", v.motorisation || "—"],
            ["1ère circulation", v.date_mise_en_circulation ? dateFr(v.date_mise_en_circulation) : "—"],
          ].map(([label, value]) => (
            <div key={label} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <p className="font-display text-xl font-bold text-slate-900 break-words">{value}</p>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-1">{label}</p>
            </div>
          ))}
        </section>

        {Object.values(ft).some(Boolean) && (
          <section className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
            <h2 className="font-display text-xl font-semibold text-slate-900">Fiche technique</h2>
            <dl className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4 text-sm">
              {[
                ["VIN", ft.vin],
                ["Carrosserie", ft.carrosserie],
                ["Puissance fiscale", ft.puissance_fiscale && `${ft.puissance_fiscale} CV`],
                ["Cylindrée", ft.cylindree && `${ft.cylindree} cm³`],
                ["Genre", ft.genre],
                ["Finition", ft.finition],
                ["Code moteur", ft.code_moteur],
                ["Places", ft.nb_places],
              ]
                .filter(([, val]) => val)
                .map(([k, val]) => (
                  <div key={k}>
                    <dt className="text-xs font-bold uppercase tracking-wider text-slate-400">{k}</dt>
                    <dd className="mt-0.5 text-slate-700 break-all">{val}</dd>
                  </div>
                ))}
            </dl>
          </section>
        )}

        {data.etat_entretien.length > 0 && (
          <section>
            <h2 className="font-display text-2xl font-bold text-slate-800 mb-5">État de l'entretien</h2>
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
              {data.etat_entretien.map((it) => (
                <div key={it.id} data-testid={`shared-suivi-${it.id}`}
                  className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-display text-lg font-semibold text-slate-900">{it.nom}</h3>
                    <StatusBadge status={it.statut} label={LABELS[it.statut]} />
                  </div>
                  <p className="mt-3 text-sm text-slate-600">
                    {it.prochain_km != null ? `Prochaine échéance : ${km(it.prochain_km)}` : ""}
                    {it.prochain_km != null && it.prochaine_date ? " · " : ""}
                    {it.prochaine_date ? dateFr(it.prochaine_date) : ""}
                  </p>
                  <p className="mt-2 text-xs text-slate-400">
                    Dernière intervention : {dateFr(it.derniere_date)} à {km(it.dernier_km)}
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        <section>
          <h2 className="font-display text-2xl font-bold text-slate-800 mb-5">Historique des opérations</h2>
          {data.operations.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center text-sm text-slate-500">
              Aucune opération enregistrée.
            </div>
          ) : (
            <ol data-testid="shared-operations" className="relative border-l border-slate-200 ml-3 space-y-6">
              {data.operations.map((o) => (
                <li key={o.id} className="ml-6">
                  <span className="absolute -left-[7px] mt-5 h-3.5 w-3.5 rounded-full bg-blue-600 border-2 border-white" />
                  <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-display text-lg font-semibold text-slate-900">{o.type_entretien}</h3>
                        <p className="text-sm text-slate-500">{dateFr(o.date)} · {km(o.kilometrage)}</p>
                      </div>
                      <span className="font-display font-bold text-slate-900 whitespace-nowrap">{eur(o.prix)}</span>
                    </div>
                    {o.garage && <p className="mt-3 text-sm text-slate-600">Garage : {o.garage}</p>}
                    {o.commentaire && <p className="mt-1 text-sm text-slate-600">{o.commentaire}</p>}
                    {o.file_name && (
                      <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-slate-500">
                        <Paperclip className="h-3.5 w-3.5" /> Justificatif conservé par le propriétaire
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>
      </main>

      <footer className="border-t border-slate-200 py-8 text-center text-sm text-slate-500">
        Carnet partagé en lecture seule via Mon Entretien Auto.
      </footer>
    </div>
  );
}
